import { ReactElement, useEffect, useLayoutEffect, useState } from 'react';
import { Component as CheckmarkIcon } from 'icons/icon-circle-lime--check.svg';
import { Component as SkippedIcon } from 'icons/icon-circle-lime--exit.svg';
import { Component as CelebrationIcon } from 'icons/icon-circle-lime--celebration.svg';
import { Component as PauseIcon } from 'icons/icon-circle-lime--pause.svg';
import { Component as ErrorIcon } from 'icons/icon-circle-lime--error.svg';
import { useQueryClient } from '@tanstack/react-query';
import { useFlagsmith } from '@flagsmith/flagsmith/react';
import { useNavigateWithUTMs } from '../../hooks/useNavigateWithUTMs';
import { Button } from '../shared/button/Button';
import { Drawer } from '../shared/drawer/Drawer';
import { CartMinimumBanner } from '../shared/banner/CartMinimumBanner';
import { FreeShippingBanner } from '../shared/banner/FreeShippingBanner';
import { UnlockedFreeShippingBanner } from '../shared/banner/UnlockedFreeShippingBanner';
import { Toast, ToastVariant } from '../shared/notification/Toast';
import CartDrawerHeaderContainer from '../cart-drawer-header/CartDrawerHeaderContainer';
import { PriceSummary } from '../price-summary/PriceSummary';
import { Cart, CartStatus, Coupon } from '../../models/cart.types';
import { CartItem } from '../cart-item/CartItem';
import { RemovedCartItem } from '../cart-item/RemovedCartItem';
import { useMenuWithCart } from '../../contexts/MenuWithCart';
import { useAuth } from '../../contexts/AuthContext';
import { SubscriptionStatus } from '../../models/user.types';
import { useNotification } from '../../contexts/NotificationContext';
import { WeeklyAddressChange } from '../weekly-address-change/WeeklyAddressChange';
import { CreateAddress } from '../create-address/CreateAddress';
import styles from './CartDrawer.module.scss';
import { useApplyUserCouponMutation } from '../../services/coupons/coupon.queries';
import { MenuCartItem } from '../../models/menu.types';

interface Props {
  cart: Cart;
  cartStatus: CartStatus | undefined;
  subscriptionStatus?: SubscriptionStatus | undefined;
  isActive: boolean;
  setCartStatus: (cartStatus: CartStatus | undefined) => void;
  isDrawerOpen: boolean;
  handleClose: (closeBySave?: boolean) => void;
  handleBeginSignup: () => void;
}

export function CartDrawer({
  cart,
  cartStatus,
  subscriptionStatus,
  isActive,
  setCartStatus,
  isDrawerOpen,
  handleClose,
  handleBeginSignup,
}: Props): ReactElement {
  const cartHeapId = `cart_${cart?.id}`;
  const { isLoggedIn } = useAuth();
  const {
    saveCart,
    updateCartQuantity,
    isCartChanged,
    isCartDrawerOpen,
    setIsCartChanged,
    isCartLoading,
    displayFreeShippingUnlockedBanner,
    setDisplayFreeShippingUnlockedBanner,
    displayFreeShippingUnlockedNotification,
    setDisplayFreeShippingUnlockedNotification,
    recalculateCart,
    displayChangeCartAddress,
    addToCart,
    numFavoriteItems: visibleUpsellLimit,
    setNumFavoriteItems: setVisibleUpsellLimit,
  } = useMenuWithCart();
  const queryClient = useQueryClient();

  const { showNotification } = useNotification();
  const remainingMinimumAmount = cart?.priceSummary?.remainingMinimumAmount;
  const isCouponFreeShipping = cart?.priceSummary?.isCouponFreeShipping;
  const bannerContent = {
    [CartStatus.UNSKIPPED]: {
      bannerText: 'Your cart is ready! We\'ve got you covered this week.',
      bannerIcon: CelebrationIcon,
      showBanner: () => !isCartChanged,
    },
    [CartStatus.SKIPPED]: {
      bannerText: 'Your order is skipped.',
      bannerIcon: SkippedIcon,
      showBanner: () => true,
    },
    [CartStatus.SAVED]: {
      bannerText: 'Your order is saved!',
      bannerIcon: CheckmarkIcon,
      showBanner: () => !isCartChanged,
    },
    [SubscriptionStatus.SUSPENDED]: {
      bannerText: 'Your account is suspended.',
      bannerIcon: ErrorIcon,
      showBanner: () => true,
    },
    [SubscriptionStatus.CANCELLED]: {
      bannerText: 'Your account is cancelled.',
      bannerIcon: SkippedIcon,
      showBanner: () => true,
    },
    [SubscriptionStatus.RETURNING]: {
      bannerText: 'Your account is paused.',
      bannerIcon: PauseIcon,
      showBanner: () => true,
    },
  };
  const [scrollToBottomVal, setScrollToBottom] = useState<boolean>(false);
  const [userCouponCode, setUserCouponCode] = useState<string>('');
  const [couponErrorMessage, setCouponErrorMessage] = useState<string>('');
  const onClose = (closeBySave?: boolean) => {
    if (!cart.isSkipped) {
      setCartStatus(undefined);
    }
    setDisplayFreeShippingUnlockedBanner(false);
    handleClose(closeBySave);
  };
  const isSaveButtonEnabled = cartStatus !== CartStatus.SKIPPED
    && isCartChanged && !remainingMinimumAmount && isActive && !isCartLoading;

  useEffect(() => {
    if (subscriptionStatus === SubscriptionStatus.ACTIVE) {
      onClose(false);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (cart.isSkipped) {
      setCartStatus(CartStatus.SKIPPED);
      setIsCartChanged(false);
    } else if (cartStatus === CartStatus.SKIPPED) {
      setCartStatus(CartStatus.UNSKIPPED);
    }
  }, [cart.isSkipped]);

  // displays free shipping unlocked notification if the coupon does not include free shipping
  useEffect(() => {
    if (displayFreeShippingUnlockedNotification && !isCouponFreeShipping) {
      showNotification({
        content: <Toast variant={ToastVariant.CART_FREE_SHIPPING_UNLOCKED} />,
        duration: 5000,
      });
      setDisplayFreeShippingUnlockedNotification(false);
    }
  }, [displayFreeShippingUnlockedNotification,
    setDisplayFreeShippingUnlockedNotification, showNotification]);
  useEffect(() => {
    setCouponErrorMessage('');
  }, [userCouponCode, isCartDrawerOpen]);

  const applyUserCouponMutation = useApplyUserCouponMutation();

  const saveOrder = () => {
    saveCart();
    setCartStatus(CartStatus.SAVED);
    onClose(true);
  };

  let content = subscriptionStatus !== undefined && !isActive
    ? bannerContent[subscriptionStatus] : null;
  if (content === null) {
    content = cartStatus ? bannerContent[cartStatus] : null;
  }

  const scrollToBottom = () => {
    const body = document.getElementById(
      'cart-scroll-area',
    ) as HTMLDivElement;

    body.scrollTo({
      top: body.scrollHeight,
      behavior: 'smooth',
    });
  };

  const { navigate } = useNavigateWithUTMs();
  const applyUserCoupon = async (couponCode: string) => {
    try {
      if (cart) {
        const updatedCart = await applyUserCouponMutation.mutateAsync({
          couponCode,
          cart,
        });

        if (updatedCart) {
          await recalculateCart(updatedCart);
          navigate(`/shop?coupon=${couponCode}`);
        } else {
          throw new Error('Error in setting coupon code');
        }
      }
    } catch (error) {
      setCouponErrorMessage('is not a valid coupon code');
    }
  };

  const handleAddToCart = async (menuItem: MenuCartItem) => {
    menuItem.quantity = 1;
    menuItem.isPromoCartItem = false;
    addToCart(menuItem);

    queryClient.invalidateQueries({ queryKey: ['menus'] });
    const currentVisibleUpsellLimit = visibleUpsellLimit - 1;
    setVisibleUpsellLimit(currentVisibleUpsellLimit);
  };

  useLayoutEffect(() => {
    if (!scrollToBottomVal) return;

    scrollToBottom();
  }, [scrollToBottomVal]);

  const isCartMinimumBannerVisible = remainingMinimumAmount && !cart.isSkipped && isActive;
  const amountAwayFromFreeShipping = cart?.priceSummary?.amountAwayFromFreeShipping;

  const flagsmith = useFlagsmith();
  const isCartUpSellDisplayEnabled = flagsmith.hasFeature('cart_upsell_display');
  // change when cart refetches in backend
  const upsellVariant = cart.cartUpsellVariant;
  const visibleUpsellItems = cart.cartItems.filter((cartItem) => cartItem.isPromoCartItem)
    .slice(0, visibleUpsellLimit);

  const shouldShowUpsells = isCartUpSellDisplayEnabled && visibleUpsellItems.length > 0;
  const getUpsellMessage = () => {
    if (upsellVariant === 'diversity') return 'Round out your box';
    if (upsellVariant === 'cheapest') return 'Quick add-ons';
    return 'Your favorite items are available!';
  };

  const cartDrawerContent = (
    <div className={styles.drawer_content} data-heap-id={cartHeapId}>
      {displayChangeCartAddress ? (
        <div className={styles.change_address_container}>
          <CreateAddress isChangeCartAddress cartShippingAddressId={cart.shippingAddressId} />
        </div>
      ) : (
        <>
          {content && content.showBanner() && (
            <div className={styles.banner}>
              <content.bannerIcon width={30} height={30} />
              {content.bannerText}
            </div>
          )}
          {isCartMinimumBannerVisible && (
            <div style={{ marginBottom: '10px' }}>
              <CartMinimumBanner remainingCartMinimum={remainingMinimumAmount} cartMinimum={cart?.priceSummary?.minimumBasePrice ?? ''} />
            </div>
          )}

          {/* Free shipping banners not displayed when coupon includes free shipping */}
          {!isCartMinimumBannerVisible && !isCouponFreeShipping && !cart.isSkipped
            && parseFloat(amountAwayFromFreeShipping.replace('$', '')) > 0
            && !(cartStatus === CartStatus.SKIPPED || !isActive) && (
              <div style={{ marginBottom: '10px' }}>
                <FreeShippingBanner
                  amountAwayFromFreeShipping={amountAwayFromFreeShipping}
                />
              </div>
          )}
          {!isCouponFreeShipping && displayFreeShippingUnlockedBanner && (
            <div style={{ marginBottom: '10px' }}>
              <UnlockedFreeShippingBanner />
            </div>
          )}

          <div className={styles.scroll_wrapper}>
            <div
              id="cart-scroll-area"
              className={[
                styles.scroll_area,
                (cartStatus === CartStatus.SKIPPED || !isActive) && styles.disabled,
              ].join(' ')}
            >

              <div className={styles.hr} />

              {cart.cartItems.filter((cartItem) => !cartItem.isPromoCartItem).map((cartItem) => (
                (cartItem?.quantity && cartItem?.quantity > 0) ? (
                  <CartItem
                    title={cartItem.title}
                    key={cartItem.title}
                    description={cartItem.description}
                    imageUrl={cartItem.imageURL}
                    totalPrice={cartItem.totalPrice}
                    skuPrice={cartItem.skuPrice}
                    pricePerServing={cartItem.pricePerServing}
                    servingSize={cartItem.servingSize}
                    quantity={cartItem.quantity || 0}
                    isCartSkipped={cart.isSkipped}
                    isSaleTagEnabled={cartItem.saleTagEnabled}
                    salePrice={cartItem.salePrice}
                    salePricePerServing={cartItem.salePricePerServing}
                    isActive={isActive}
                    onUpdateCartQuantity={(quantity: number) => {
                      updateCartQuantity(cartItem, quantity);
                    }}
                    heapId={`cart_item_${cartItem.skuId}`}
                    skuId={cartItem.skuId}
                    path={cartItem.productPath}
                    onClickImage={() => onClose()}
                    isLtoBox={cartItem.isLtoBox}
                    ltoServings={cartItem.ltoServings}
                    isPromoItem={!!cartItem.isPromoCartItem}
                  />
                ) : (
                  <RemovedCartItem
                    title={cartItem.title}
                    key={cartItem.title}
                    onClick={() => {
                      updateCartQuantity(cartItem, 1);
                    }}
                  />
                )
              ))}
              <br />
              {shouldShowUpsells && <b>{getUpsellMessage()}</b>}

              {shouldShowUpsells && visibleUpsellItems.map((cartItem) => (
                <CartItem
                  title={cartItem.title}
                  key={cartItem.title}
                  description={cartItem.description}
                  imageUrl={cartItem.imageURL}
                  totalPrice={cartItem.totalPrice}
                  skuPrice={cartItem.skuPrice}
                  pricePerServing={cartItem.pricePerServing}
                  servingSize={cartItem.servingSize}
                  quantity={cartItem.quantity || 0}
                  isCartSkipped={cart.isSkipped}
                  isSaleTagEnabled={cartItem.saleTagEnabled}
                  salePrice={cartItem.salePrice}
                  salePricePerServing={cartItem.salePricePerServing}
                  isActive={isActive}
                  onAddToCart={() => handleAddToCart(cartItem)}
                  onUpdateCartQuantity={(quantity: number) => {
                    updateCartQuantity(cartItem, quantity);
                  }}
                  heapId={`cart_item_${cartItem.skuId}`}
                  skuId={cartItem.skuId}
                  path={cartItem.productPath}
                  onClickImage={() => onClose()}
                  isLtoBox={cartItem.isLtoBox}
                  ltoServings={cartItem.ltoServings}
                  isPromoItem={!!cartItem.isPromoCartItem}
                />
              ))}

              <div className={styles.breakdown_spacing}>
                <PriceSummary
                  subTotal={cart.priceSummary.subtotal}
                  shipping={cart.priceSummary.shipping}
                  couponCode={cart.priceSummary.couponName}
                  discounts={cart.priceSummary.totalDiscountAmount}
                  couponAmount={cart.priceSummary.couponAmount}
                  isCouponApplied={cart.priceSummary.isCouponApplied}
                  extendedAreaSurcharge={cart.priceSummary.extendedAreaSurcharge}
                  setScrollToBottom={setScrollToBottom}
                  userCouponCode={userCouponCode}
                  setUserCouponCode={setUserCouponCode}
                  couponErrorMessage={couponErrorMessage}
                  applyUserCoupon={applyUserCoupon}
                />
              </div>
              {isLoggedIn && (
                <div className={styles.breakdown_spacing}>
                  <WeeklyAddressChange />
                </div>
              )}
            </div>
          </div>
          <div className={styles.footer}>
            {isLoggedIn ? (
              <Button
                text={cartStatus === CartStatus.SKIPPED
                  ? 'Order Skipped   -   $0.00'
                  : `Save Order - ${cart?.priceSummary.total}`}
                onClick={() => saveOrder()}
                isEnabled={isSaveButtonEnabled}
              />
            ) : (
              <Button
                text={`Check Out - ${cart?.priceSummary.total}`}
                onClick={handleBeginSignup}
                isEnabled={!remainingMinimumAmount && !isCartLoading}
              />
            )}
            <div>
              {`Free shipping on orders over ${cart?.priceSummary.freeShippingThreshold}. $15 surcharge in certain areas.`}
            </div>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Drawer
      trapFocus={false}
      isOpen={isDrawerOpen}
      onClose={() => onClose()}
      header={<CartDrawerHeaderContainer />}
      content={cartDrawerContent}
    />
  );
}
